import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import PhotoOutlinedIcon from "@mui/icons-material/PhotoOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import MicOutlinedIcon from "@mui/icons-material/MicOutlined";
import MicOffOutlinedIcon from "@mui/icons-material/MicOffOutlined";
import { useTranslation } from "react-i18next";
import {
  isSpeechDictationSupported,
  startMeetingSpeechDictation,
} from "./meetingSpeechDictation";

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const rise = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const pop = keyframes`
  0%, 12% { opacity: 0.3; transform: translateY(6px) scale(0.92); }
  35%, 68% { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0.4; transform: translateY(3px) scale(0.96); }
`;

const writeLine = keyframes`
  0%, 15% { width: 18%; opacity: 0.35; }
  40%, 70% { width: 72%; opacity: 1; }
  100% { width: 48%; opacity: 0.45; }
`;

const bob = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

const pulseMic = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.35); }
  50% { box-shadow: 0 0 0 10px rgba(211, 47, 47, 0); }
`;

function NotePreviewStage({ isVideo, accent }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 84,
        mt: 1.35,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: alpha(accent, 0.1),
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
        animation: `${rise} 320ms ease both`,
        ...motionSafe,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 14,
          top: 14,
          width: 44,
          height: 56,
          borderRadius: 1.25,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.4),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 6px 14px ${alpha(accent, 0.18)}`,
          animation: `${pop} 3.6s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        {isVideo ? (
          <VideocamOutlinedIcon sx={{ fontSize: 22, color: accent }} />
        ) : (
          <PhotoOutlinedIcon sx={{ fontSize: 22, color: accent }} />
        )}
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: 72,
          right: 16,
          top: 18,
          bottom: 18,
          borderRadius: 1.25,
          bgcolor: alpha("#fff", 0.92),
          border: "1px solid",
          borderColor: alpha(accent, 0.28),
          px: 1.1,
          py: 1,
          display: "flex",
          flexDirection: "column",
          gap: 0.7,
          justifyContent: "center",
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              height: 7,
              borderRadius: 999,
              bgcolor: alpha(accent, 0.28 + i * 0.1),
              animation: `${writeLine} 2.8s ease-in-out infinite`,
              animationDelay: `${i * 0.18}s`,
              ...motionSafe,
            }}
          />
        ))}
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: 10,
          bottom: 10,
          width: 28,
          height: 28,
          borderRadius: 1,
          bgcolor: alpha(accent, 0.16),
          border: "1px solid",
          borderColor: alpha(accent, 0.4),
          display: "grid",
          placeItems: "center",
          animation: `${bob} 2.2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <EditNoteOutlinedIcon sx={{ fontSize: 16, color: accent }} />
      </Box>
    </Box>
  );
}

/**
 * Dialog post-captura / drop: Nota STAGE + dictado por voz (siempre es-ES).
 */
export default function MeetingTeamNoteCaptureDialog({
  open,
  onClose,
  onSave,
  value = "",
  onChange,
  saving = false,
  isNarrow = false,
  isVideo = false,
  uploading = false,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const accent = theme.palette.primary.main || "#0f766e";
  const [listening, setListening] = useState(false);
  const [speechError, setSpeechError] = useState("");
  const [interimPreview, setInterimPreview] = useState("");
  const dictationRef = useRef(null);
  const baseAtListenRef = useRef("");
  const valueRef = useRef(value);
  const interimRef = useRef("");
  const speechSupported = isSpeechDictationSupported();

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const stopListening = (opts = {}) => {
    const flushInterim = opts.flushInterim !== false;
    dictationRef.current?.stop?.();
    dictationRef.current = null;
    setListening(false);
    if (flushInterim) {
      const pending = String(interimRef.current || "").trim();
      if (pending) {
        const next = [String(valueRef.current || "").trim(), pending]
          .filter(Boolean)
          .join(" ")
          .replace(/\s+/g, " ")
          .trim();
        onChange?.({ target: { value: next } });
      }
    }
    interimRef.current = "";
    setInterimPreview("");
  };

  useEffect(() => {
    if (!open) stopListening({ flushInterim: false });
    return () => stopListening({ flushInterim: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleListen = () => {
    if (saving) return;
    if (listening) {
      stopListening();
      return;
    }
    setSpeechError("");
    setInterimPreview("");
    interimRef.current = "";
    baseAtListenRef.current = String(value || "").trim();
    dictationRef.current = startMeetingSpeechDictation({
      lang: "es-ES",
      getBaseText: () => "",
      onUpdate: ({ finalText, interimText }) => {
        // Solo confirmados en el campo (el provisional va aparte → menos saltos)
        const next = [baseAtListenRef.current, finalText]
          .map((s) => String(s || "").trim())
          .filter(Boolean)
          .join(" ")
          .replace(/\s+/g, " ")
          .trim();
        onChange?.({ target: { value: next } });
        interimRef.current = String(interimText || "").trim();
        setInterimPreview(interimRef.current);
      },
      onError: (key) => {
        setSpeechError(
          key === "unsupported"
            ? t("Meetings.teamNoteVoiceUnsupported")
            : t("Meetings.teamNoteVoiceError"),
        );
      },
      onListeningChange: setListening,
    });
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: (th) => alpha(th.palette.background.default, 0.55),
      animation: `${rise} 340ms ease both`,
      animationDelay: "80ms",
      ...motionSafe,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      fullScreen={isNarrow}
      maxWidth="sm"
      keepMounted={false}
      transitionDuration={160}
      className="meeting-no-print"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: isNarrow ? 0 : 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          maxHeight: isNarrow ? "100%" : "min(92vh, 640px)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          flexShrink: 0,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: (th) =>
            `linear-gradient(135deg, ${alpha(th.palette.primary.main, 0.12)} 0%, ${alpha(
              th.palette.success.main,
              0.08,
            )} 100%)`,
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(accent, 0.16),
                color: accent,
                flexShrink: 0,
              }}
            >
              <NotesOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, lineHeight: 1.15 }}
              >
                {t("Meetings.teamNoteCaptureTitle")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.4, mt: 0.3 }}
              >
                {isVideo
                  ? t("Meetings.teamNoteCaptureBodyVideo")
                  : t("Meetings.teamNoteCaptureBodyPhoto")}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label={t("Meetings.cancel")}
            onClick={onClose}
            disabled={saving}
            size="small"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Stack>
        {uploading ? (
          <Chip
            size="small"
            label={t("Meetings.teamNoteCaptureUploading")}
            sx={{
              mt: 1.1,
              height: 24,
              fontWeight: 800,
              borderRadius: 1.25,
              bgcolor: alpha(accent, 0.12),
            }}
          />
        ) : null}
        <NotePreviewStage isVideo={isVideo} accent={accent} />
      </Box>

      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1,
          overflow: "auto",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={isNarrow ? 5 : 4}
            maxRows={16}
            label={t("Meetings.teamNote")}
            placeholder={t("Meetings.teamNoteHint")}
            value={value}
            onChange={onChange}
            disabled={saving}
            inputProps={{ maxLength: 8000 }}
            sx={fieldSx}
          />
          <Tooltip
            title={
              speechSupported
                ? listening
                  ? t("Meetings.teamNoteVoiceStop")
                  : t("Meetings.teamNoteVoiceStart")
                : t("Meetings.teamNoteVoiceUnsupported")
            }
          >
            <span>
              <IconButton
                color={listening ? "error" : "primary"}
                onClick={toggleListen}
                disabled={saving || !speechSupported}
                aria-pressed={listening}
                aria-label={
                  listening
                    ? t("Meetings.teamNoteVoiceStop")
                    : t("Meetings.teamNoteVoiceStart")
                }
                sx={{
                  mt: 0.5,
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  border: "1.5px solid",
                  borderColor: listening ? "error.main" : alpha(accent, 0.35),
                  bgcolor: listening
                    ? alpha(theme.palette.error.main, 0.12)
                    : alpha(accent, 0.08),
                  animation: listening
                    ? `${pulseMic} 1.4s ease-out infinite`
                    : "none",
                  ...motionSafe,
                }}
              >
                {listening ? <MicOffOutlinedIcon /> : <MicOutlinedIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        {listening ? (
          <Stack spacing={0.75} sx={{ mt: 1.1 }}>
            <Chip
              size="small"
              color="error"
              icon={<MicOutlinedIcon sx={{ fontSize: "16px !important" }} />}
              label={t("Meetings.teamNoteVoiceListening")}
              sx={{ fontWeight: 800, alignSelf: "flex-start" }}
            />
            {interimPreview ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontStyle: "italic",
                  lineHeight: 1.4,
                  px: 0.25,
                }}
              >
                {interimPreview}
              </Typography>
            ) : null}
          </Stack>
        ) : null}
        {speechError ? (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: "block", mt: 1, fontWeight: 700 }}
          >
            {speechError}
          </Typography>
        ) : null}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            mt: 1.25,
            lineHeight: 1.4,
            fontWeight: 600,
          }}
        >
          {t("Meetings.teamNoteCaptureTip")}
        </Typography>
      </Box>

      <Stack
        direction={{ xs: "column-reverse", sm: "row" }}
        spacing={1.25}
        justifyContent="flex-end"
        sx={{
          px: 2.5,
          py: 1.75,
          borderTop: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
          bgcolor: (th) => alpha(th.palette.background.default, 0.65),
        }}
      >
        <Button
          fullWidth={isNarrow}
          onClick={() => {
            stopListening();
            onClose?.();
          }}
          disabled={saving}
          sx={{ fontWeight: 700, minHeight: 44 }}
        >
          {t("Meetings.skip")}
        </Button>
        <Button
          fullWidth={isNarrow}
          variant="contained"
          onClick={() => {
            stopListening();
            onSave?.();
          }}
          disabled={saving}
          sx={{
            minHeight: 48,
            fontWeight: 800,
            borderRadius: 2,
            boxShadow: `0 8px 18px ${alpha(accent, 0.28)}`,
          }}
        >
          {t("Meetings.saveNote")}
        </Button>
      </Stack>
    </Dialog>
  );
}
